/**
 * MeshFactory - Centralized mesh creation utilities for combat effects
 * 
 * Provides mesh cloning, entity-based mesh creation, and video material application.
 * Follows fail-fast philosophy - throws on missing assets, no fallback meshes.
 */

import { Scene, AbstractMesh, MeshBuilder } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { 
  isEmbeddedReference, 
  parseEmbeddedReference, 
  getEntity, 
  type ExtractionResult 
} from '../assets/EntityAssetExtractor';
import { 
  applyVideoMaterialToMesh, 
  playVideoOnMesh, 
  extractVideoConfigFromMesh 
} from '../assets/VideoTextureUtils';

export interface ProjectileMeshSpec {
  /** Asset path - must be embedded reference like "entity://projectile_id" */
  assetPath?: string;
  /** Scale factor (default: 1.0) */
  scale?: number;
}

/**
 * Create a mesh from a projectile specification.
 * Supports embedded entity references with video material support.
 * 
 * @param scene - The Babylon.js scene
 * @param spec - Projectile mesh specification
 * @param name - Name for the created mesh
 * @param embeddedEntities - Entity extraction result containing available entities
 * @returns The created mesh
 * @throws If assetPath is missing, entity not found, or entity has no meshes
 */
export function createProjectileMesh(
  scene: Scene,
  spec: ProjectileMeshSpec,
  name: string,
  embeddedEntities?: ExtractionResult
): AbstractMesh {
  if (!spec.assetPath) {
    throw new Error(`MeshFactory: Projectile spec missing assetPath. All projectiles must specify an asset.`);
  }

  if (!isEmbeddedReference(spec.assetPath)) {
    throw new Error(
      `MeshFactory: External asset path not supported for projectiles: ${spec.assetPath}. ` +
      `Use embedded entity reference (e.g., "entity://projectile_id").`
    );
  }

  const { entityId } = parseEmbeddedReference(spec.assetPath);
  logger.combat.debug(`[MeshFactory] Loading embedded projectile: '${entityId}'`);

  if (!embeddedEntities) {
    throw new Error(
      `MeshFactory: No embeddedEntities provided for projectile '${entityId}'. ` +
      `Attack requires embedded entities.`
    );
  }

  // getEntity throws if not found - fail-fast
  const entity = getEntity(embeddedEntities, entityId);

  logger.combat.debug(
    `[MeshFactory] Found entity '${entityId}': root=${entity.root.name} (${entity.root.getClassName()}), ` +
    `meshes=${entity.meshes.length}`
  );
  entity.meshes.forEach((m: AbstractMesh, i: number) => {
    logger.combat.debug(`  [${i}] ${m.name} (${m.getClassName()})`);
  });

  // Clone meshes and parent them to a movable container mesh
  // We use an invisible mesh as container so it can be attached to bones, moved, etc.
  const containerMesh = MeshBuilder.CreateBox(name, { size: 0.001 }, scene);
  containerMesh.isVisible = false;
  containerMesh.isPickable = false;

  const meshesToClone = entity.meshes;

  if (meshesToClone.length === 0) {
    containerMesh.dispose();
    throw new Error(`MeshFactory: Entity '${entityId}' has no meshes. Cannot create projectile.`);
  }

  for (let i = 0; i < meshesToClone.length; i++) {
    const srcMesh = meshesToClone[i];

    logger.combat.debug(`  Checking srcMesh[${i}] "${srcMesh.name}" for video config`);
    logger.combat.debug(`    metadata:`, srcMesh.metadata);
    logger.combat.debug(`    gltf.extras:`, srcMesh.metadata?.gltf?.extras);
    if (srcMesh.material) {
      logger.combat.debug(`    material "${srcMesh.material.name}" metadata:`, (srcMesh.material as any).metadata);
    }

    const clonedMesh = srcMesh.clone(`${name}_mesh_${i}`, containerMesh, true) as AbstractMesh;
    if (clonedMesh) {
      // Reset the local position relative to container (keep original offset from entity root)
      const srcRoot = entity.root;
      if (srcRoot !== srcMesh && srcMesh.parent === srcRoot) {
        // Mesh was direct child of root - keep its local position
        clonedMesh.position.copyFrom(srcMesh.position);
      } else {
        // Mesh was the root itself or nested differently - reset to origin
        clonedMesh.position.set(0, 0, 0);
      }
      clonedMesh.setEnabled(true);
      clonedMesh.isVisible = true;

      // Check if source mesh has video material configured (hm_materialVideo_* on mesh or material)
      const videoConfig = extractVideoConfigFromMesh(srcMesh);
      if (videoConfig) {
        const videoTexture = applyVideoMaterialToMesh(clonedMesh, videoConfig);
        if (videoTexture) {
          playVideoOnMesh(clonedMesh);
          logger.combat.debug(`  Applied video material "${videoConfig.src}" to cloned mesh: ${clonedMesh.name}`);
        }
      }

      logger.combat.debug(
        `  Cloned mesh: ${clonedMesh.name}, ` +
        `pos=(${clonedMesh.position.x.toFixed(2)}, ${clonedMesh.position.y.toFixed(2)}, ${clonedMesh.position.z.toFixed(2)}), ` +
        `material=${clonedMesh.material?.name ?? 'none'}`
      );
    }
  }

  logger.combat.debug(`[MeshFactory] Created projectile container with ${meshesToClone.length} meshes: ${containerMesh.name}`);

  // Apply scale
  const scale = spec.scale ?? 1.0;
  containerMesh.scaling.copyFromFloats(scale, scale, scale);
  containerMesh.isPickable = false;
  containerMesh.checkCollisions = false;

  return containerMesh;
}

/**
 * Clone an entity's meshes into a container mesh.
 * Used for static FX, attached meshes, etc.
 * 
 * @param scene - The Babylon.js scene
 * @param entityId - The entity ID to clone from
 * @param name - Name for the container mesh
 * @param embeddedEntities - Entity extraction result
 * @param options - Optional configuration
 * @returns The container mesh with cloned children
 * @throws If entity not found or has no meshes
 */
export function cloneEntityToMesh(
  scene: Scene,
  entityId: string,
  name: string,
  embeddedEntities: ExtractionResult,
  options?: {
    scale?: number;
    applyVideoMaterials?: boolean;
  }
): AbstractMesh {
  const entity = getEntity(embeddedEntities, entityId);
  
  if (entity.meshes.length === 0) {
    throw new Error(`MeshFactory: Entity '${entityId}' has no meshes.`);
  }

  const containerMesh = MeshBuilder.CreateBox(name, { size: 0.001 }, scene);
  containerMesh.isVisible = false;
  containerMesh.isPickable = false;

  for (let i = 0; i < entity.meshes.length; i++) {
    const srcMesh = entity.meshes[i];
    const clonedMesh = srcMesh.clone(`${name}_mesh_${i}`, containerMesh, true) as AbstractMesh;
    
    if (clonedMesh) {
      const srcRoot = entity.root;
      if (srcRoot !== srcMesh && srcMesh.parent === srcRoot) {
        clonedMesh.position.copyFrom(srcMesh.position);
      } else {
        clonedMesh.position.set(0, 0, 0);
      }
      clonedMesh.setEnabled(true);
      clonedMesh.isVisible = true;

      if (options?.applyVideoMaterials !== false) {
        const videoConfig = extractVideoConfigFromMesh(srcMesh);
        if (videoConfig) {
          const videoTexture = applyVideoMaterialToMesh(clonedMesh, videoConfig);
          if (videoTexture) {
            playVideoOnMesh(clonedMesh);
          }
        }
      }
    }
  }

  const scale = options?.scale ?? 1.0;
  containerMesh.scaling.copyFromFloats(scale, scale, scale);
  containerMesh.isPickable = false;
  containerMesh.checkCollisions = false;

  return containerMesh;
}

/**
 * Get all child meshes from a container (including video textures for cleanup)
 */
export function getContainerMeshes(container: AbstractMesh): AbstractMesh[] {
  return container.getChildMeshes(false) as AbstractMesh[];
}
